import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { Loadable, Tabbar, TabPanel, useTabs, useLoad, fmtDay, SelectField } from "@/components/control/kit";
import { V3Page, V3Aside, Field, Notice, FeatureRadios, V3Actions, useRun, ErrorLine, TextArea, VALUE_TEXT } from "@/components/control/v3";

function Header({ loc }) {
  return (
    <div className="flex items-center gap-4 rounded-2xl border border-[#E6E1F0] bg-white p-4" data-testid="location-header">
      <span className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl bg-[#EEEAF7] text-brand-magenta" aria-hidden="true"><MapPin className="h-8 w-8" /></span>
      <div className="min-w-0"><p className="text-2xl font-bold text-brand-purple">{loc.name}</p>
        <p className="mt-1 text-sm text-zinc-600">{[loc.address, loc.city].filter(Boolean).join(", ") || "Adresse noch nicht erfasst"}</p></div>
    </div>
  );
}

function ProfileTab({ loc, org, features, roles, reload }) {
  const navigate = useNavigate();
  const current = loc.access?.features || {};
  const [values, setValues] = useState(() => Object.fromEntries(Object.keys(features).map((k) => [k, current[k]?.value || "unbekannt"])));
  const [responsible, setResponsible] = useState(loc.responsible_member_id || "");
  const [source, setSource] = useState("");
  const [reason, setReason] = useState("");
  const { busy, error, run } = useRun();
  const eligible = roles.filter((r) => r.status === "bestätigt" && (r.role === "leitung" || (r.role === "standortkoordination" && r.location_ids.includes(loc.id))));
  const last = Object.values(current).filter((f) => f?.updated_at).sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1))[0];
  const save = async () => {
    const body = { features: values, source, reason, responsible_member_id: responsible || null };
    if (await run(() => api.put(`/locations/${loc.id}/review`, body), "Prüfung gespeichert.")) reload();
  };
  return (
    <div className="space-y-2.5">
      <Field label="Standort">{loc.name}</Field>
      <Field label="Organisation">{org.name}</Field>
      <SelectField id="location-responsible" label="Zuständig" value={responsible} onChange={setResponsible} testId="location-responsible"
        options={[{ value: "", label: eligible.length ? "Noch nicht festgelegt" : "Keine bestätigte Leitung oder Koordination" },
          ...eligible.map((r) => ({ value: r.member_id, label: `${r.member_name} · ${r.role_label}` }))]} />
      {Object.entries(features).map(([k, l]) => (
        <div key={k} className="space-y-1">
          <FeatureRadios id={k} label={l} value={values[k]} onChange={(v) => setValues((s) => ({ ...s, [k]: v }))} />
          <p className="px-1 text-xs text-zinc-600" data-testid={`location-source-${k}`}>
            {current[k]?.updated_at ? `${VALUE_TEXT[current[k].value]} · Quelle: ${current[k].source} · Stand ${fmtDay(current[k].updated_at)}` : "Unbekannt · Quelle und Stand dokumentieren"}
          </p>
        </div>
      ))}
      <label htmlFor="location-source-input" className="flex flex-col gap-1">
        <span className="text-sm font-semibold text-brand-purple">Quelle der Angaben *</span>
        <input id="location-source-input" value={source} onChange={(e) => setSource(e.target.value)} data-testid="location-source-input"
          placeholder="z. B. Eigene Angabe der Organisation oder Vor-Ort-Prüfung" className="h-11 rounded-xl border border-[#E6E1F0] bg-white px-3 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-magenta" />
      </label>
      <TextArea id="location-reason" label="Begründung" value={reason} onChange={setReason} />
      <Notice title="Quelle und Zuständigkeit" testId="location-source-note"
        text={`${last ? `${last.source} · Stand ${fmtDay(last.updated_at)}` : "Noch keine geprüften Angaben"}. Eine Organisationsbestätigung ist keine Zugangszertifizierung.`} />
      <ErrorLine error={error} testId="location-error" />
      <V3Actions primary="Prüfung speichern" onPrimary={save} busy={busy} secondary="Zurück" onSecondary={() => navigate(`/organisationen/${org.id}`)} testId="location" />
    </div>
  );
}

function LocationView({ org, data, roles, locationId, reload }) {
  const [tab, setTab] = useState("profil");
  const tabs = useTabs([{ value: "profil", label: "Profil" }, { value: "standorte", label: "Standorte" }, { value: "kontakt", label: "Kontakt" }], tab);
  const loc = data.locations.find((l) => l.id === locationId);
  if (!loc) return <Notice title="Standort nicht gefunden" text="Dieser Standort gehört nicht zu dieser Organisation." testId="location-missing" />;
  return (
    <V3Page crumb="Organisationen" title="Standort und Zuständigkeit prüfen" subtitle="Standortangaben und berechtigte Zuständigkeit werden unabhängig geprüft."
      aside={<V3Aside subject={loc.name} />} testId="location-review">
      <Header loc={loc} />
      <Tabbar tabs={tabs} onChange={setTab} testId="location-tabs" ariaLabel="Standortbereiche" />
      <TabPanel tabs={tabs} testId="location-tab-panel">
        {tabs.value === "profil" && <ProfileTab key={loc.id + (loc.reviewed_at || "")} loc={loc} org={org} features={data.features} roles={roles} reload={reload} />}
        {tabs.value === "standorte" && (
          <ul className="space-y-2.5">{data.locations.map((l) => (
            <li key={l.id}><Link to={`/organisationen/${org.id}/standorte/${l.id}`} data-testid={`location-link-${l.id}`}
              className="block rounded-xl border border-[#E6E1F0] bg-white px-4 py-3 font-semibold text-brand-purple hover:border-brand-magenta">{l.name} · {l.city || "–"}</Link></li>))}</ul>
        )}
        {tabs.value === "kontakt" && (
          <div className="space-y-2.5"><Field label="Verantwortliche Stelle">{org.responsibility}</Field><Field label="Website">{org.website}</Field>
            <Field label="Zuständig für den Standort">{loc.responsible_name || "Noch nicht festgelegt"}</Field></div>
        )}
      </TabPanel>
    </V3Page>
  );
}

export default function LocationReview() {
  const { id, locationId } = useParams();
  const org = useLoad(`/organizations/${id}`);
  const locs = useLoad(`/organizations/${id}/locations`);
  const roles = useLoad(`/organizations/${id}/roles`);
  const reload = () => { locs.reload(); roles.reload(); };
  return (
    <Loadable state={org}>
      {(o) => (
        <Loadable state={locs}>
          {(l) => <LocationView org={o.organization} data={l} roles={roles.data?.roles || []} locationId={locationId} reload={reload} />}
        </Loadable>
      )}
    </Loadable>
  );
}
